import slugify from '@sindresorhus/slugify';
import React, { useCallback, useEffect, useState } from 'react';
import { useHistory, useLocation, useParams, useRouteMatch } from 'react-router-dom/cjs/react-router-dom.min';
import { useToast, useWorkspace } from '~/contexts';
import { PageLoader } from '~/routes/public/pages';
import { useReports } from './ReportsContext';

export const ReportContext = React.createContext();

export default function ReportPage() {
  const { area, slug } = useParams();
  const { workspace } = useWorkspace();
  const reportsContext = useReports();
  const toast = useToast();
  const history = useHistory();
  const { search } = useLocation();
  const { url } = useRouteMatch();

  const [report, setReport] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const report = reportsContext.reports.find((report) => report.slug === slug);
      if (!report) {
        toast.error('Report not found.');
        history.replace(`/app/${workspace.key}/reports`);
        return;
      }

      const source = {
        pathname: url,
        search,
      };

      const target = { ...source };

      // Adjust pathname based on scope
      switch (report.scopeKey) {
        case 'workspace':
        case 'member':
          target.pathname = `/app/${workspace.key}/reports/${area}/${report.slug}/${slugify(report.name)}`;
          break;
        default:
          target.pathname = `/app/${workspace.key}/reports/${area}/${report.slug}`;
          break;
      }

      // Adjust search if empty and report has stored params
      if (!search && report.params?.length) {
        target.search = `?${new URLSearchParams(report.params).toString()}`;
      }

      if (
        source.pathname !== target.pathname ||
        new URLSearchParams(source.search).toString() !== new URLSearchParams(target.search).toString()
      ) {
        history.replace(target);
      }

      setReport(report);
    } catch (error) {
      toast.error(error?.message ?? 'Failed to load report.');
      history.replace(`/app/${workspace.key}/reports`);
    }
  }, [workspace.key, slug, reportsContext?.reports, url, toast, history, area, search]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (!report) return <PageLoader />;

  const Component = report.component;

  return (
    <ReportContext.Provider value={{ report, onRefetch: reportsContext.fetchData }}>
      <Component report={report} />
    </ReportContext.Provider>
  );
}
